import React from 'react';
import { Box, Chip } from '@mui/material';
import { Trans } from '@lingui/macro';
import RocketIcon from '/public/icons/rocket.svg';

import { EPositionType, IEnrichedUniswapPosition } from '../../../../types/uniswapTokens';
import { POOL_TYPE_FILTERS } from '../../../../modules/leverage/lists/ToLeveragePositionsList/constants';
import { FormattedNumber } from '../../../primitives/FormattedNumber';
import { MobileItemWrapper } from '../../MobileItemWrapper';
import { useEstAprValues } from '../../hooks/useEstAprValues';
import { MobileValueRow } from '../MobileValueRow';

export interface IUniswapPositionsListItem {
  position: IEnrichedUniswapPosition;
  actions: (args: {
    position: IEnrichedUniswapPosition;
    tokenId?: string;
    positionAddress?: string;
    isEmptyPosition?: boolean;
    uniswapLink?: string;
  }) => React.ReactNode;
  columnsType: 'lending' | 'leverage' | 'leveraged';
}

export const PositionsListItemMobile = ({ position, actions }: IUniswapPositionsListItem) => {
  const { estNetApr } = useEstAprValues({ position });
  const poolTypeLabel = POOL_TYPE_FILTERS.find((item) => item.value === position.poolType)?.label;

  return (
    <MobileItemWrapper
      position={position}
    >
      {position.type === EPositionType.LeveragedPosition ? (
        <>
          <MobileValueRow caption={<Trans>Liquidity</Trans>}>
            <FormattedNumber
              value={position.marketReferenceCurrencyValue}
              symbol="USD"
              variant="secondary14"
              compact
            />
          </MobileValueRow>
          <MobileValueRow caption={<Trans>Debt Value</Trans>}>
            <FormattedNumber
              value={position.marketReferenceCurrencyDebt || 0}
              symbol="USD"
              variant="secondary14"
              compact
            />
          </MobileValueRow>
          <MobileValueRow caption={<Trans>Fees Earned</Trans>}>
            <FormattedNumber
              value={position.token0.marketReferenceCurrencyFee + position.token1.marketReferenceCurrencyFee}
              symbol="USD"
              variant="secondary14"
              compact
            />
          </MobileValueRow>
          <MobileValueRow caption={<Trans>Net Worth</Trans>}>
            <FormattedNumber
              value={position.token0.netWorthUsd + position.token1.netWorthUsd}
              symbol="USD"
              variant="secondary14"
              compact
            />
          </MobileValueRow>
          <MobileValueRow caption={<Trans>Est. Net APR</Trans>}>
            <FormattedNumber
              value={estNetApr}
              variant="secondary14"
              color={estNetApr > 0 ? 'text.brand' : 'text.tertiary' }
              symbolsColor={estNetApr > 0 ? 'text.brand' : 'text.tertiary' }
              percent
              compact
            />
          </MobileValueRow>
          <MobileValueRow caption={<Trans>Health</Trans>}>
            <FormattedNumber
              value={position.healthFactor || 0}
              variant="secondary14"
              compact
            />
          </MobileValueRow>
        </>
      ) : (
        <>
          {poolTypeLabel &&
            <MobileValueRow caption={<Trans>Type</Trans>}>
              <Chip label={poolTypeLabel}/>
            </MobileValueRow>
          }
          <MobileValueRow caption={<Trans>Liquidity</Trans>}>
            <FormattedNumber
              value={position.marketReferenceCurrencyValue}
              symbol="USD"
              variant="secondary14"
              color={position.marketReferenceCurrencyValue <= 0 ? 'text.tertiary' : undefined}
              compact
            />
          </MobileValueRow>
          <MobileValueRow caption={<Trans>Max Leverage</Trans>}>
            <FormattedNumber
              value={position.maxLeverage}
              variant="secondary14"
              postfix="x"
              compact
            />
          </MobileValueRow>
          <MobileValueRow caption={<Trans>Max Net APR</Trans>}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormattedNumber
                value={estNetApr}
                variant="secondary14"
                color={estNetApr > 0 ? 'text.brand' : 'text.tertiary' }
                symbolsColor={estNetApr > 0 ? 'text.brand' : 'text.tertiary' }
                percent
                compact
              />
              {estNetApr > 0 && <Box sx={{ ml: 1, mb: '-3px' }}><RocketIcon /></Box>}
            </Box>
          </MobileValueRow>
        </>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
        {actions({
          position,
          tokenId: position.tokenId.toString(),
          positionAddress: position.positionAddress,
          isEmptyPosition: position.type === EPositionType.Pool,
          uniswapLink: position.uniswapLink,
        })}
      </Box>
    </MobileItemWrapper>
  );
};
